import React from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import { useRerunnerConfigs } from "hooks/useRerunnerConfigs";
import { Lock, Settings, Pencil } from "lucide-react";

export default function RerunnerDetail() {
  const navigate = useNavigate();
  const { configs, loading, featureGated } = useRerunnerConfigs();
  const config = configs?.[0];

  if (loading) {
    return (
      <div>
        <PageHeader title="Rerunner" />
        <div className="container py-6 space-y-4">
          <Skeleton.Rect className="h-48 w-full" rounded="xl" />
        </div>
      </div>
    );
  }

  if (featureGated) {
    return (
      <div>
        <PageHeader title="Rerunner" />
        <div className="container py-6">
          <div className="surface rounded-xl ring-1 ring-border p-12 text-center">
            <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Feature Not Available</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              The Rerunner feature is not enabled for your account. Contact your account manager to learn more about automated decline recovery.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div>
        <PageHeader title="Rerunner" />
        <div className="container py-6">
          <div className="surface rounded-xl ring-1 ring-border p-12 text-center">
            <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Configuration</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Your rerunner configuration hasn't been set up yet. Contact your account manager to get started.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Rerunner"
        subtitle={config.clientName}
        actions={
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/rerunner/${config.clientId}/edit`)}
          >
            <Pencil className="h-4 w-4" />
            Edit Settings
          </button>
        }
      />

      <div className="container py-6 space-y-6">
        {/* Status */}
        <section className="surface rounded-xl ring-1 ring-border p-6">
          <h2 className="text-lg font-semibold mb-4">Status</h2>
          <div className="flex items-center gap-3">
            <span className={`badge ${config.enabled ? "badge-brand" : "badge-muted"}`}>
              {config.enabled ? "Active" : "Disabled"}
            </span>
            <span className="text-sm text-muted-foreground">
              Time window: {config.timeWindowHours}h
            </span>
          </div>
        </section>

        {/* Filters */}
        {config.filters && (
          <section className="surface rounded-xl ring-1 ring-border p-6">
            <h2 className="text-lg font-semibold mb-4">Filters</h2>
            <div className="grid grid-cols-2 gap-4">
              {config.filters.minAmount != null && (
                <div>
                  <p className="text-sm text-muted-foreground">Min Amount</p>
                  <p className="text-sm font-medium">${config.filters.minAmount}</p>
                </div>
              )}
              {config.filters.maxAmount != null && (
                <div>
                  <p className="text-sm text-muted-foreground">Max Amount</p>
                  <p className="text-sm font-medium">${config.filters.maxAmount}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Decline Codes */}
        <section className="surface rounded-xl ring-1 ring-border p-6">
          <h2 className="text-lg font-semibold mb-4">Decline Codes</h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Retriable</p>
              <div className="flex flex-wrap gap-1">
                {(config.retriableDeclineCodes || []).map((code) => (
                  <span key={code} className="badge badge-muted font-mono text-xs">{code}</span>
                ))}
              </div>
            </div>
            {config.excludedDeclineCodes?.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Excluded</p>
                <div className="flex flex-wrap gap-1">
                  {config.excludedDeclineCodes.map((code) => (
                    <span key={code} className="badge badge-muted font-mono text-xs">{code}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Rate Limit */}
        {config.rateLimit && (
          <section className="surface rounded-xl ring-1 ring-border p-6">
            <h2 className="text-lg font-semibold mb-4">Rate Limit</h2>
            <div className="grid grid-cols-2 gap-4">
              {config.rateLimit.requestsPerSecond != null && (
                <div>
                  <p className="text-sm text-muted-foreground">Requests/sec</p>
                  <p className="text-sm font-medium">{config.rateLimit.requestsPerSecond}</p>
                </div>
              )}
              {config.rateLimit.maxConcurrent != null && (
                <div>
                  <p className="text-sm text-muted-foreground">Max Concurrent</p>
                  <p className="text-sm font-medium">{config.rateLimit.maxConcurrent}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Schedule */}
        {config.schedule && (
          <section className="surface rounded-xl ring-1 ring-border p-6">
            <h2 className="text-lg font-semibold mb-4">Schedule</h2>
            <p className="text-sm font-mono">{config.schedule}</p>
          </section>
        )}
      </div>
    </div>
  );
}
